import { ChangeEvent, useMemo } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import {
  FormControl,
  FormHelperText,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { set, snakeCase } from 'lodash'

import { DefaultElementProps } from 'common/interfaces'
import { selectionSetStyle } from 'common/styles'
import RadioGroupRows from 'elements/RadioGroupSet/components/RadioGroupRow'
import validationsToMapRules from 'utils/validationsToRules'

import { RadioGroupSet as RadioGroupSetProps } from './interface'

export default function RadioGroupSet({
  name,
  validations,
  variants,
  labels,
  label,
  onChange,
  onFocus,
  defaultValue,
}: RadioGroupSetProps & DefaultElementProps): JSX.Element {
  const { width100, variantText, variantTextWrap } = selectionSetStyle

  const defaultValues = useMemo(() => {
    if (typeof defaultValue === 'string' && variants.includes(defaultValue)) {
      return labels.reduce((acc: Record<string, string>, currentValue) => {
        const radioGroupName = snakeCase(currentValue)
        acc[radioGroupName] = defaultValue
        return acc
      }, {})
    }

    return defaultValue
  }, [])

  const { control } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control: control,
    defaultValue: defaultValues ?? {},
    rules: validationsToMapRules(validations),
  })

  function handleChange(event: ChangeEvent<HTMLInputElement>, groupValue: string): void {
    const newValue = set(field.value, event.target.name, groupValue)
    field.onChange(newValue)
    onChange?.({ value: newValue })
  }

  return (
    <FormControl
      className="radio-set-wrap"
      onFocus={onFocus}
      error={error !== undefined}
      sx={width100}
    >
      {label !== undefined && <FormLabel>{label}</FormLabel>}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={width100} />
            <TableCell sx={variantTextWrap}>
              {variants.map((variant) => (
                <Typography variant="body2" sx={variantText} key={variant}>
                  {variant}
                </Typography>
              ))}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {labels.map((item) => (
            <RadioGroupRows
              item={item}
              variants={variants}
              value={field.value}
              handleChange={handleChange}
              key={item}
            />
          ))}
        </TableBody>
      </Table>
      {error !== undefined && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
  )
}
